import type React from "react";

import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Maximize2,
  HelpCircle,
  BarChart4,
  Table as TableIcon,
  Image as ImageIcon,
  ArrowDown,
  ArrowRight,
} from "lucide-react";
import type {
  Slide,
  Theme,
  ChartData,
} from "@/routes/~dashboard/~presentation/presentation-data";
import { cn } from "@/lib/utils";
import AreaChart from "@/components/charts/area-chart";
import BarChartComponent from "@/components/charts/bar-chart";
import LineChart from "@/components/charts/line-chart";
import PieChartComponent from "@/components/charts/pie-chart";
import DoughnutChart from "@/components/charts/doughnut-chart";

// Extend the Slide interface with timeline properties
interface ExtendedSlide extends Slide {
  milestone1Upper?: string;
  milestone2Upper?: string;
  milestone3Upper?: string;
  milestone4Upper?: string;
  milestone1Lower?: string;
  milestone2Lower?: string;
  milestone3Lower?: string;
  milestone4Lower?: string;
  milestone1Date?: string;
  milestone2Date?: string;
  milestone3Date?: string;
  milestone4Date?: string;
  milestone1Text?: string;
  milestone2Text?: string;
  milestone3Text?: string;
  milestone4Text?: string;
  step1Box?: string;
  step2Box?: string;
  step3Box?: string;
  step4Box?: string;
  step1Text?: string;
  step2Text?: string;
  step3Text?: string;
  step4Text?: string;
  thankYouText?: string;
  contactInfo?: string;
}

interface SlidePreviewProps {
  slides: ExtendedSlide[];
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  theme: Theme;
}

interface VisibleSlide {
  slide: ExtendedSlide;
  position: "previous" | "current" | "next";
  index: number;
}

export default function SlidePreview({
  slides,
  currentIndex,
  setCurrentIndex,
  theme,
}: SlidePreviewProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const [transitionDirection, setTransitionDirection] = useState<
    "next" | "prev" | null
  >(null);

  const handlePrevSlide = useCallback(() => {
    if (currentIndex > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setTransitionDirection("prev");
      setCurrentIndex(currentIndex - 1);
      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 300);
    }
  }, [currentIndex, isTransitioning, setCurrentIndex]);

  const handleNextSlide = useCallback(() => {
    if (currentIndex < slides.length - 1 && !isTransitioning) {
      setIsTransitioning(true);
      setTransitionDirection("next");
      setCurrentIndex(currentIndex + 1);
      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 300);
    }
  }, [currentIndex, isTransitioning, setCurrentIndex, slides.length]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePrevSlide();
      } else if (e.key === "ArrowRight") {
        handleNextSlide();
      } else if (e.key === "Escape" && isFullscreen) {
        exitFullscreen();
      } else if (e.key === "f") {
        toggleFullscreen();
      } else if (e.key === "h") {
        setShowShortcutsHelp((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    currentIndex,
    isFullscreen,
    handlePrevSlide,
    handleNextSlide,
    toggleFullscreen,
    exitFullscreen,
  ]);

  const getSlideStyle = (slide: ExtendedSlide) => {
    return {
      backgroundColor: slide.background || theme.slideBackground,
      color: slide.textColor || theme.textColor,
      fontFamily: theme.fontFamily,
    };
  };

  const getLayoutClass = (layout: string) => {
    switch (layout) {
      case "title-only":
        return "flex flex-col justify-center items-center text-center";
      case "title-content":
        return "flex flex-col justify-start items-center";
      case "split":
        return "grid grid-cols-2 gap-4";
      case "image-text":
        return "grid grid-cols-2 gap-4";
      case "titleSlide":
        return "flex flex-col justify-center items-center text-center";
      case "contentFull":
        return "flex flex-col justify-start items-center";
      case "contentTwoCol":
        return "grid grid-cols-2 gap-8";
      case "contentThreeCol":
        return "grid grid-cols-3 gap-4";
      case "contentFourCol":
        return "grid grid-cols-4 gap-3";
      case "contentWithMediaRight":
        return "grid grid-cols-2 gap-8";
      case "contentWithMediaLeft":
        return "grid grid-cols-2 gap-8";
      case "imageWithCaption":
      case "videoWithCaption":
        return "flex flex-col justify-start items-center";
      case "imageGrid":
        return "grid grid-rows-2 grid-cols-3 gap-4";
      case "table":
        return "flex flex-col justify-start items-center";
      case "chartSingle":
        return "flex flex-col justify-start items-center";
      case "chartDual":
        return "grid grid-cols-2 gap-8";
      case "dashboard":
        return "grid grid-cols-2 grid-rows-2 gap-4";
      case "processHorizontal":
        return "flex flex-col justify-start items-center";
      case "processVertical":
        return "flex flex-col justify-start items-center";
      case "timelineHorizontal":
        return "flex flex-col justify-start items-center";
      case "timelineVertical":
        return "flex flex-col justify-start items-center";
      case "contactInfo":
        return "flex flex-col justify-center items-center text-center";
      default:
        return "flex flex-col justify-start";
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    // Swipe threshold
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNextSlide();
      } else {
        handlePrevSlide();
      }
    }

    touchStartX.current = null;
  };

  const getVisibleSlides = (): VisibleSlide[] => {
    const visibleSlides: VisibleSlide[] = [];

    if (!slides || slides.length === 0) {
      return visibleSlides;
    }

    // Previous slide
    if (currentIndex > 0) {
      visibleSlides.push({
        slide: slides[currentIndex - 1],
        position: "previous",
        index: currentIndex - 1,
      });
    }

    // Current slide
    visibleSlides.push({
      slide: slides[currentIndex],
      position: "current",
      index: currentIndex,
    });

    // Next slide
    if (currentIndex < slides.length - 1) {
      visibleSlides.push({
        slide: slides[currentIndex + 1],
        position: "next",
        index: currentIndex + 1,
      });
    }

    return visibleSlides;
  };

  // Helper function to render chart placeholder
  const renderChartPlaceholder = (chartData: ChartData | undefined) => {
    if (!chartData) {
      return (
        <div className="bg-muted/20 flex h-full w-full flex-col items-center justify-center rounded-md">
          <BarChart4 className="text-muted-foreground/60 h-10 w-10" />
          <div className="mt-2 text-center text-muted-foreground">
            <div className="font-medium">No Chart Data</div>
          </div>
        </div>
      );
    }

    // Transform the chart data to the format expected by chart components
    const chartSeries = chartData.data?.map((series) => ({
      name: series.name,
      data: series.labels.map((label, idx) => ({
        name: label,
        value: series.values[idx] || 0,
      })),
    }));

    const chartProps = {
      series: chartSeries,
      height: "100%",
      showGrid: true,
      animate: true,
    };

    // Render the appropriate chart type
    switch (chartData.type) {
      case "bar":
        return <BarChartComponent {...chartProps} height={300} />;
      case "line":
        return <LineChart {...chartProps} height={300} />;
      case "pie":
        return <PieChartComponent {...chartProps} height={300} />;
      case "doughnut":
        return <DoughnutChart {...chartProps} height={300} />;
      case "area":
        return <AreaChart {...chartProps} height={300} />;
      default:
        return (
          <div className="bg-muted/20 flex h-full w-full flex-col items-center justify-center rounded-md">
            <BarChart4 className="text-muted-foreground/60 h-10 w-10" />
            <div className="mt-2 text-center text-muted-foreground">
              <div className="font-medium">{chartData.type || "Chart"}</div>
              <div className="mt-1 text-sm">
                {chartData.data?.[0]?.labels?.length || 0} data points
              </div>
            </div>
          </div>
        );
    }
  };

  const renderTablePlaceholder = (slide: ExtendedSlide) => {
    if (!slide.table) return null;

    return (
      <div className="w-full overflow-x-auto rounded-lg bg-white p-2 shadow-inner">
        <div className="flex flex-col items-center justify-center gap-2 p-6">
          <TableIcon className="text-muted-foreground/60 h-10 w-10" />
          <div className="text-center text-muted-foreground">
            <div className="font-medium">Table</div>
            <div className="mt-1 text-sm">
              {slide.table.rows.length} rows ×{" "}
              {slide.table.rows[0]?.length || 0} columns
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMediaPlaceholder = (type: string = "image", path?: string) => {
    if (path && path.length > 0) {
      // If path starts with blob: or data:, it's a media that was uploaded
      if (path.startsWith("blob:") || path.startsWith("data:")) {
        if (type === "video") {
          return (
            <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-md">
              <video
                src={path}
                controls
                className="max-h-full max-w-full object-contain"
              />
            </div>
          );
        } else {
          // Default to image
          return (
            <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-md">
              <img
                src={path}
                alt="Slide media"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          );
        }
      }
    }

    // Fallback to placeholder if no media or invalid path
    return (
      <div className="bg-muted/20 flex h-full w-full flex-col items-center justify-center rounded-md">
        <ImageIcon className="text-muted-foreground/60 h-10 w-10" />
        <div className="mt-2 text-center capitalize text-muted-foreground">
          {type}
        </div>
      </div>
    );
  };

  // Render actual media (images and videos) instead of placeholders
  const renderMedia = (
    mediaItem: { path: string; type: string } | undefined,
    type: string = "image",
  ) => {
    if (!mediaItem || !mediaItem.path) {
      return renderMediaPlaceholder(type);
    }

    // Check if it's a real URL or base64 data URL
    const isRealMedia =
      mediaItem.path.startsWith("http") ||
      mediaItem.path.startsWith("blob:") ||
      mediaItem.path.startsWith("data:");

    if (!isRealMedia) {
      return renderMediaPlaceholder(type);
    }

    if (mediaItem.type === "video") {
      return (
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-md">
          <video
            src={mediaItem.path}
            controls
            className="max-h-full max-w-full"
            style={{ maxHeight: "100%" }}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    } else {
      return (
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-md bg-black/5">
          <img
            src={mediaItem.path}
            alt="Slide media"
            className="max-h-full max-w-full object-contain"
            style={{ maxHeight: "100%" }}
          />
        </div>
      );
    }
  };

  const renderProcess = (
    slide: ExtendedSlide,
    direction: "horizontal" | "vertical",
  ) => {
    const ArrowIcon = direction === "horizontal" ? ArrowRight : ArrowDown;

    return (
      <div
        className={`w-full ${direction === "horizontal" ? "flex items-center justify-between" : "flex flex-col items-center gap-4"}`}
      >
        <div className="bg-primary/10 flex flex-col items-center rounded-md p-4">
          <div className="font-bold">{slide.step1Box}</div>
          <div className="text-sm">{slide.step1Text}</div>
        </div>

        <ArrowIcon className="mx-2 h-6 w-6 text-muted-foreground" />

        <div className="bg-primary/10 flex flex-col items-center rounded-md p-4">
          <div className="font-bold">{slide.step2Box}</div>
          <div className="text-sm">{slide.step2Text}</div>
        </div>

        <ArrowIcon className="mx-2 h-6 w-6 text-muted-foreground" />

        <div className="bg-primary/10 flex flex-col items-center rounded-md p-4">
          <div className="font-bold">{slide.step3Box}</div>
          <div className="text-sm">{slide.step3Text}</div>
        </div>

        {slide.step4Box && direction === "horizontal" && (
          <>
            <ArrowIcon className="mx-2 h-6 w-6 text-muted-foreground" />
            <div className="bg-primary/10 flex flex-col items-center rounded-md p-4">
              <div className="font-bold">{slide.step4Box}</div>
              <div className="text-sm">{slide.step4Text}</div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderTimeline = (
    slide: ExtendedSlide,
    direction: "horizontal" | "vertical",
  ) => {
    if (direction === "horizontal") {
      return (
        <div className="relative mt-8 w-full">
          <div className="absolute left-0 right-0 top-6 h-1 bg-muted-foreground"></div>
          <div className="flex justify-between">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="flex flex-col items-center">
                <div className="mb-2 text-center text-sm">
                  {slide[
                    `milestone${num}Upper` as keyof ExtendedSlide
                  ]?.toString()}
                </div>
                <div className="z-10 h-3 w-3 rounded-full bg-primary"></div>
                <div className="mt-2 text-center text-sm">
                  {slide[
                    `milestone${num}Lower` as keyof ExtendedSlide
                  ]?.toString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      return (
        <div className="relative ml-4 mt-4 w-full">
          <div className="absolute bottom-0 left-4 top-0 w-1 bg-muted-foreground"></div>
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className="mb-8 flex">
              <div className="mr-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  {num}
                </div>
              </div>
              <div>
                <div className="font-bold">
                  {slide[
                    `milestone${num}Date` as keyof ExtendedSlide
                  ]?.toString()}
                </div>
                <div>
                  {slide[
                    `milestone${num}Text` as keyof ExtendedSlide
                  ]?.toString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  const renderSlideContent = (slide: ExtendedSlide) => {
    switch (slide.layout) {
      case "title-only":
      case "standard":
        return (
          <>
            <h2
              className="mb-6 text-center text-5xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {slide.content && (
              <p className="whitespace-pre-line text-center text-2xl">
                {slide.content}
              </p>
            )}
          </>
        );

      case "title-content":
        return (
          <>
            <h2
              className="mb-4 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            <p className="whitespace-pre-line text-xl">{slide.content}</p>
          </>
        );

      case "split":
        return (
          <>
            <div className="p-4">
              <h2
                className="mb-4 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
            </div>
            <div className="p-4">
              <p className="whitespace-pre-line text-xl">{slide.content}</p>
            </div>
          </>
        );

      case "image-text":
        return (
          <>
            {renderMedia(slide.media, "image")}
            <div className="p-4">
              <h2
                className="mb-4 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
              <p className="whitespace-pre-line text-xl">{slide.content}</p>
            </div>
          </>
        );

      case "titleSlide":
        return (
          <>
            <h1
              className="mb-4 text-center text-6xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h1>
            {slide.subtitle && (
              <h2 className="text-center text-3xl font-light">
                {slide.subtitle}
              </h2>
            )}
          </>
        );

      case "contentFull":
        return (
          <>
            <h2
              className="mb-2 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {slide.subtitle && (
              <h3 className="mb-6 text-2xl font-light">{slide.subtitle}</h3>
            )}
            {slide.content && (
              <p className="whitespace-pre-line text-xl">{slide.content}</p>
            )}
          </>
        );

      case "contentTwoCol":
        return (
          <>
            <div className="col-span-2 mb-4">
              <h2
                className="mb-2 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
              {slide.subtitle && (
                <h3 className="text-xl font-light">{slide.subtitle}</h3>
              )}
            </div>
            <div>
              <p className="whitespace-pre-line">{slide.colLeft}</p>
            </div>
            <div>
              <p className="whitespace-pre-line">{slide.colRight}</p>
            </div>
          </>
        );

      case "contentThreeCol":
        return (
          <>
            <div className="col-span-3 mb-4">
              <h2
                className="mb-2 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
              {slide.subtitle && (
                <h3 className="text-xl font-light">{slide.subtitle}</h3>
              )}
            </div>
            <div>
              <p className="whitespace-pre-line">{slide.colLeft}</p>
            </div>
            <div>
              <p className="whitespace-pre-line">{slide.colMiddle}</p>
            </div>
            <div>
              <p className="whitespace-pre-line">{slide.colRight}</p>
            </div>
          </>
        );

      case "contentFourCol":
        return (
          <>
            <div className="col-span-4 mb-4">
              <h2
                className="mb-2 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
              {slide.subtitle && (
                <h3 className="text-xl font-light">{slide.subtitle}</h3>
              )}
            </div>
            <div>
              <p className="whitespace-pre-line text-sm">{slide.col1}</p>
            </div>
            <div>
              <p className="whitespace-pre-line text-sm">{slide.col2}</p>
            </div>
            <div>
              <p className="whitespace-pre-line text-sm">{slide.col3}</p>
            </div>
            <div>
              <p className="whitespace-pre-line text-sm">{slide.col4}</p>
            </div>
          </>
        );

      case "contentWithMediaRight":
        return (
          <>
            <div className="col-span-2 mb-4">
              <h2
                className="mb-2 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
              {slide.subtitle && (
                <h3 className="mb-4 text-xl font-light">{slide.subtitle}</h3>
              )}
            </div>
            <div>
              <p className="whitespace-pre-line">{slide.content}</p>
            </div>
            <div>{renderMedia(slide.media, slide.media?.type)}</div>
          </>
        );

      case "contentWithMediaLeft":
        return (
          <>
            <div className="col-span-2 mb-4">
              <h2
                className="mb-2 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
              {slide.subtitle && (
                <h3 className="mb-4 text-xl font-light">{slide.subtitle}</h3>
              )}
            </div>
            <div>{renderMedia(slide.media, slide.media?.type)}</div>
            <div>
              <p className="whitespace-pre-line">{slide.content}</p>
            </div>
          </>
        );

      case "imageWithCaption":
      case "videoWithCaption":
        return (
          <>
            <h2
              className="mb-4 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            <div className="mb-4 w-3/4">
              {renderMedia(
                slide.media,
                slide.layout === "imageWithCaption" ? "image" : "video",
              )}
            </div>
            {slide.caption && (
              <p className="text-center text-xl italic">{slide.caption}</p>
            )}
          </>
        );

      case "imageGrid":
        return (
          <>
            <h2
              className="col-span-3 mb-6 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {[
              "cell_1_1",
              "cell_1_2",
              "cell_1_3",
              "cell_2_1",
              "cell_2_2",
              "cell_2_3",
            ].map((idx) => (
              <div key={idx} className="h-full w-full">
                {typeof slide[idx as keyof ExtendedSlide] === "object" &&
                slide[idx as keyof ExtendedSlide] !== null &&
                "path" in (slide[idx as keyof ExtendedSlide] as object) &&
                (
                  slide[idx as keyof ExtendedSlide] as { path: string }
                ).path.startsWith("blob:") ? (
                  <div className="h-full w-full overflow-hidden rounded-md">
                    <img
                      src={
                        (slide[idx as keyof ExtendedSlide] as { path: string })
                          .path
                      }
                      alt={`Grid image ${idx}`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  renderMediaPlaceholder("image")
                )}
              </div>
            ))}
          </>
        );

      case "table":
        return (
          <>
            <h2
              className="mb-2 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {slide.subtitle && (
              <h3 className="mb-4 text-2xl font-light">{slide.subtitle}</h3>
            )}
            {renderTablePlaceholder(slide)}
          </>
        );

      case "chartSingle":
        return (
          <>
            <h2
              className="mb-2 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {slide.subtitle && (
              <h3 className="mb-4 text-xl font-light">{slide.subtitle}</h3>
            )}
            <div className="mb-4 h-64 w-3/4">
              {renderChartPlaceholder(slide.chart)}
            </div>
            {slide.explanationTitle && (
              <>
                <h4 className="mb-2 text-xl font-semibold">
                  {slide.explanationTitle}
                </h4>
                <p className="whitespace-pre-line">
                  {slide.explanationContent}
                </p>
              </>
            )}
          </>
        );

      case "chartDual":
        return (
          <>
            <div className="col-span-2 mb-4">
              <h2
                className="mb-4 text-4xl font-bold"
                style={{ color: slide.titleColor || theme.titleColor }}
              >
                {slide.title}
              </h2>
            </div>
            <div>
              <h3 className="mb-2 text-xl font-semibold">{slide.titleLeft}</h3>
              <div className="mb-4 h-40 w-full">
                {renderChartPlaceholder(slide.chartLeft)}
              </div>
              <p className="whitespace-pre-line text-sm">
                {slide.explanationLeft}
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-xl font-semibold">{slide.titleRight}</h3>
              <div className="mb-4 h-40 w-full">
                {renderChartPlaceholder(slide.chartRight)}
              </div>
              <p className="whitespace-pre-line text-sm">
                {slide.explanationRight}
              </p>
            </div>
          </>
        );

      case "dashboard":
        return (
          <>
            <h2
              className="col-span-2 mb-4 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            <div className="h-full w-full">
              {renderChartPlaceholder(slide.chart1)}
            </div>
            <div className="h-full w-full">
              {renderChartPlaceholder(slide.chart2)}
            </div>
            <div className="h-full w-full">
              {renderChartPlaceholder(slide.chart3)}
            </div>
            <div className="h-full w-full">
              {renderChartPlaceholder(slide.chart4)}
            </div>
          </>
        );

      case "processHorizontal":
        return (
          <>
            <h2
              className="mb-6 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {renderProcess(slide, "horizontal")}
          </>
        );

      case "processVertical":
        return (
          <>
            <h2
              className="mb-6 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {renderProcess(slide, "vertical")}
          </>
        );

      case "timelineHorizontal":
        return (
          <>
            <h2
              className="mb-6 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {renderTimeline(slide, "horizontal")}
          </>
        );

      case "timelineVertical":
        return (
          <>
            <h2
              className="mb-6 text-4xl font-bold"
              style={{ color: slide.titleColor || theme.titleColor }}
            >
              {slide.title}
            </h2>
            {renderTimeline(slide, "vertical")}
          </>
        );

      case "contactInfo":
        return (
          <>
            <h1
              className="mb-6 text-6xl font-bold"
              style={{ color: theme.accentColor }}
            >
              {slide.thankYouText}
            </h1>
            <div className="whitespace-pre-line text-xl">
              {slide.contactInfo}
            </div>
          </>
        );

      default:
        return (
          <div className="flex h-full w-full flex-col items-center justify-center text-center">
            <p className="text-2xl font-semibold text-muted-foreground">
              Unknown layout type: {slide.layout}
            </p>
          </div>
        );
    }
  };

  if (!slides || !Array.isArray(slides)) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-muted-foreground">No slides available</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full flex-col items-center justify-center p-4",
        isFullscreen ? "fixed inset-0 z-50 bg-black/95" : "",
      )}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Fullscreen mode */}
      {isFullscreen && (
        <>
          <div className="absolute left-4 right-4 top-4 z-10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="bg-black/20 text-white hover:bg-black/40"
                onClick={handlePrevSlide}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="bg-black/20 text-white hover:bg-black/40"
                onClick={handleNextSlide}
                disabled={currentIndex === slides.length - 1}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
              <span className="ml-2 text-sm font-medium text-white/80">
                {currentIndex + 1} / {slides.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="bg-black/20 text-white hover:bg-black/40"
                onClick={() => setShowShortcutsHelp((prev) => !prev)}
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="bg-black/20 text-white hover:bg-black/40"
                onClick={exitFullscreen}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          <div className="flex h-full w-full items-center justify-center p-8">
            <div
              className="relative w-full max-w-6xl"
              style={{ maxHeight: "80vh" }}
            >
              <div className="relative w-full pb-[56.25%]">
                <Card
                  className="absolute inset-0 overflow-hidden rounded-xl shadow-2xl"
                  style={getSlideStyle(slides[currentIndex])}
                >
                  <CardContent
                    className={cn(
                      "absolute inset-0 p-10",
                      getLayoutClass(slides[currentIndex].layout),
                    )}
                  >
                    {renderSlideContent(slides[currentIndex])}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevSlide}
            disabled={currentIndex === 0}
            className="absolute left-4 top-1/2 z-30 h-16 w-16 -translate-y-1/2 rounded-full bg-black/10 text-white hover:bg-black/30"
          ></Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextSlide}
            disabled={currentIndex === slides.length - 1}
            className="absolute right-4 top-1/2 z-30 h-16 w-16 -translate-y-1/2 rounded-full bg-black/10 text-white hover:bg-black/30"
          ></Button>

          {showShortcutsHelp && (
            <div className="absolute inset-x-0 bottom-8 z-40 mx-auto flex max-w-md items-center justify-center gap-6 rounded-lg bg-black/60 p-4 text-white">
              <div className="flex items-center gap-1">
                <kbd className="rounded border border-white/30 bg-white/20 px-2 py-1 font-mono">
                  ←
                </kbd>
                <span>Previous</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="rounded border border-white/30 bg-white/20 px-2 py-1 font-mono">
                  →
                </kbd>
                <span>Next</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="rounded border border-white/30 bg-white/20 px-2 py-1 font-mono">
                  Esc
                </kbd>
                <span>Exit</span>
              </div>
            </div>
          )}
        </>
      )}

      {!isFullscreen && (
        <>
          <div className="mb-4 flex w-full max-w-6xl items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevSlide}
              disabled={currentIndex === 0 || isTransitioning}
              className="group relative flex items-center gap-2 transition-all hover:translate-x-[-2px]"
            >
              <div className="group-hover:bg-primary/5 absolute inset-0 rounded-full opacity-0 transition-opacity group-hover:opacity-100" />
              <ChevronLeft className="h-4 w-4 transition-transform group-hover:scale-125" />
              <span className="text-sm">Previous</span>
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {currentIndex + 1} / {slides.length}
              </span>
              <div className="flex h-2 gap-1">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    className={cn(
                      "h-2 rounded-full transition-all",
                      idx === currentIndex
                        ? "w-6 bg-primary"
                        : "hover:bg-primary/50 w-2 bg-muted",
                    )}
                    onClick={() => setCurrentIndex(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNextSlide}
              disabled={currentIndex === slides.length - 1 || isTransitioning}
              className="group relative flex items-center gap-2 transition-all hover:translate-x-[2px]"
            >
              <div className="group-hover:bg-primary/5 absolute inset-0 rounded-full opacity-0 transition-opacity group-hover:opacity-100" />
              <span className="text-sm">Next</span>
              <ChevronRight className="h-4 w-4 transition-transform group-hover:scale-125" />
            </Button>
          </div>

          <div
            ref={carouselRef}
            className="flex w-full max-w-6xl items-center justify-center py-8"
            style={{ height: "65vh" }}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevSlide}
              disabled={currentIndex === 0}
              className="bg-background/80 absolute left-0 z-30 h-12 w-12 rounded-full shadow-md hover:bg-background"
            >
              <span className="sr-only">Previous slide</span>
            </Button>

            <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
              {getVisibleSlides().map(({ slide, position, index }) => {
                let xPosition = "0%";
                let zIndex = position === "current" ? 20 : 10;
                const scale = position === "current" ? 1 : 0.7;
                const opacity = position === "current" ? 1 : 0.6;
                const hoverScale = position === "current" ? 1.02 : 0.75;

                if (position === "previous") {
                  xPosition =
                    isTransitioning && transitionDirection === "prev"
                      ? "0%"
                      : "-35%";
                  zIndex =
                    isTransitioning && transitionDirection === "prev" ? 15 : 10;
                } else if (position === "next") {
                  xPosition =
                    isTransitioning && transitionDirection === "next"
                      ? "0%"
                      : "35%";
                  zIndex =
                    isTransitioning && transitionDirection === "next" ? 15 : 10;
                }

                const cardWidth = position === "current" ? "75%" : "50%";

                return (
                  <div
                    key={index}
                    className={cn(
                      "absolute transition-all duration-300 ease-in-out",
                    )}
                    style={{
                      transform: `translateX(${xPosition}) scale(${scale})`,
                      zIndex: zIndex,
                      width: cardWidth,
                      opacity: opacity,
                    }}
                  >
                    <div className="relative w-full pb-[56.25%]">
                      {" "}
                      <Card
                        className={cn(
                          "absolute inset-0 overflow-hidden rounded-xl transition-all duration-300 ease-in-out",
                          position === "current" ? "shadow-xl" : "shadow-md",
                          isTransitioning ? "pointer-events-none" : "",
                        )}
                        style={{
                          ...getSlideStyle(slide),
                          cursor:
                            position === "current" ? "pointer" : "default",
                        }}
                        onClick={() => {
                          if (position === "previous") handlePrevSlide();
                          else if (position === "next") handleNextSlide();
                          else if (position === "current") toggleFullscreen();
                        }}
                        onMouseEnter={(e) => {
                          if (position === "current" && !isTransitioning) {
                            const parent =
                              e.currentTarget.parentElement?.parentElement;
                            if (parent) {
                              parent.style.transform = `translateX(0%) scale(${hoverScale})`;
                            }
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (position === "current" && !isTransitioning) {
                            const parent =
                              e.currentTarget.parentElement?.parentElement;
                            if (parent) {
                              parent.style.transform = `translateX(0%) scale(1)`;
                            }
                          }
                        }}
                      >
                        <CardContent
                          className={cn(
                            "absolute inset-0 p-8",
                            getLayoutClass(slide.layout),
                          )}
                        >
                          {renderSlideContent(slide)}
                        </CardContent>

                        {position !== "current" && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/5">
                            {position === "previous" && (
                              <div className="bg-background/80 rounded-full p-2">
                                <ChevronLeft className="h-6 w-6" />
                              </div>
                            )}
                            {position === "next" && (
                              <div className="bg-background/80 rounded-full p-2">
                                <ChevronRight className="h-6 w-6" />
                              </div>
                            )}
                          </div>
                        )}

                        {position === "current" && (
                          <div className="absolute right-3 top-3 opacity-60 transition-opacity hover:opacity-100">
                            <Maximize2 className="h-5 w-5" />
                          </div>
                        )}
                      </Card>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleNextSlide}
              disabled={currentIndex === slides.length - 1}
              className="bg-background/80 absolute right-0 z-30 h-12 w-12 rounded-full shadow-md hover:bg-background"
            >
              <span className="sr-only">Next slide</span>
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="hover:bg-primary/10 group mt-4 gap-2 transition-all"
            onClick={toggleFullscreen}
          >
            <Maximize2 className="h-4 w-4 transition-transform group-hover:scale-110" />
            <span>Present Slides</span>
          </Button>

          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <kbd className="rounded border px-1.5 py-0.5 font-mono">←</kbd>
              <span>Previous</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="rounded border px-1.5 py-0.5 font-mono">→</kbd>
              <span>Next</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="rounded border px-1.5 py-0.5 font-mono">F</kbd>
              <span>Fullscreen</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
